/**
 * The book: all 314 examples, each with the verdict the corpus run recorded for it.
 *
 * The headline rate is the book marking its own homework, which is the point of the whole
 * exercise — so it is printed with the two figures that keep it honest beside it: how many
 * examples came back flawless, and how much of the music the rules were able to speak about.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CatalogueRow } from "@app/shared-types";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import { Failed, Loading, Note } from "../components/Page";
import { count, rate } from "../lib/format";

type Filter = "all" | "flawless" | "faulted";

export default function BookPage(): JSX.Element {
  const { data, error, loading } = useAsync("catalogue", () => api.catalogue());
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo<CatalogueRow[]>(() => {
    const all = data?.examples ?? [];
    const needle = query.trim().toLowerCase();
    return all.filter((row) => {
      if (filter === "flawless" && row.flawless !== true) return false;
      if (filter === "faulted" && !(row.faultCount !== null && row.faultCount > 0)) return false;
      if (!needle) return true;
      return `${row.number} ${row.caption} ${row.work ?? ""}`.toLowerCase().includes(needle);
    });
  }, [data, query, filter]);

  const shell = (children: JSX.Element): JSX.Element => (
    <Board kicker="Rimsky-Korsakov" title="The book" measure="wide">
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what="the book" />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="the catalogue came back empty" />);

  const summary = data.summary;
  const flawlessCount = data.examples.filter((one) => one.flawless === true).length;
  const faultedCount = data.examples.filter(
    (one) => one.faultCount !== null && one.faultCount > 0,
  ).length;

  return (
    <Board
      kicker="Rimsky-Korsakov"
      title="The book"
      lede={`${count(data.examples.length)} examples, every one of them run through all eight predicates.`}
      measure="wide"
    >
      <div className="space-y-6">
        {summary ? (
          <div className="rounded-xl border border-rule bg-leaf p-5">
            <div className="flex items-baseline gap-1">
              <span className="font-mono text-[40px] leading-none">
                {rate(summary.score).replace("%", "")}
              </span>
              <span className="font-mono text-lg text-ink-quiet">%</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-faded">
              overall, one vote per rule · {count(summary.flawless)} of {count(summary.examples)}{" "}
              flawless · coverage {rate(summary.coverage)}
            </p>
          </div>
        ) : (
          <Note>{data.note}</Note>
        )}

        {/* 314 rows is a long scroll, so what narrows them stays on the glass while it is
          scrolled rather than being left behind at the top of the list. */}
        <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-2 bg-paper px-1 py-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search the examples"
            aria-label="Search the examples"
            className="h-10 min-w-[12rem] flex-1 rounded-[10px] border border-rule bg-leaf px-3.5 text-sm outline-none focus:border-ink"
          />
          {(
            [
              ["all", `All ${data.examples.length}`],
              ["flawless", `Flawless ${flawlessCount}`],
              ["faulted", `With faults ${faultedCount}`],
            ] as const
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              className={`h-10 shrink-0 rounded-full border px-3.5 text-[13px] font-medium ${
                filter === key
                  ? "border-ink bg-ink text-paper"
                  : "border-rule bg-leaf text-ink-faded"
              }`}
            >
              {text}
            </button>
          ))}
        </div>

        <ul className="overflow-hidden rounded-xl border border-rule bg-leaf">
          {rows.length === 0 ? (
            <li className="px-4 py-6 text-sm text-ink-faded">Nothing matches that.</li>
          ) : (
            rows.map((row) => (
              <li
                key={`${row.number}${row.variant ?? ""}`}
                className="border-b border-rule-faint last:border-b-0"
              >
                {row.id === null ? (
                  <div className="flex min-h-[64px] items-center gap-3 px-4 py-3 opacity-60">
                    <span className="w-10 shrink-0 font-mono text-xs text-ink-quiet">
                      {row.number}
                      {row.variant ?? ""}
                    </span>
                    <span className="flex-1 text-sm">{row.caption}</span>
                    <span className="font-mono text-xs text-unread">no score</span>
                  </div>
                ) : (
                  <Link
                    to={`/book/${row.id}`}
                    className="flex min-h-[64px] items-center gap-3 px-4 py-3 hover:bg-rule-faint/50"
                  >
                    <span className="w-10 shrink-0 font-mono text-xs text-ink-quiet">
                      {row.number}
                      {row.variant ?? ""}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm leading-snug">{row.caption}</span>
                      <span className="mt-0.5 block text-xs text-ink-quiet">
                        {[row.work, row.page ? `p. ${row.page}` : null].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 font-mono text-sm ${
                        row.flawless ? "text-clean" : "text-ink-faded"
                      }`}
                    >
                      {rate(row.score)}
                    </span>
                  </Link>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    </Board>
  );
}
